import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useLiveRefresh } from "@/lib/live";
import { useAuth, hasRole } from "@/context/AuthContext";
import { canAccess } from "@/config/roleViews";
import { Button } from "@/components/ui/button";
import { Package2, PackageMinus, Undo2 } from "lucide-react";
import { bprMovementsPath } from "@/components/JobLink";
import { IssueDialog, ReturnDialog } from "@/components/stores/StoreDialogs";
import { ORANGE_BTN, RollsLink, qty, unit } from "@/components/stores/storesUi";

const inr = (n) => (n ?? 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

// Raw material against one job, from the stores ledger: what the job card plans,
// what stores issued roll by roll and what came back. Issue / return here posts the
// same stores entries as Inventory → Movements, so the stores queue, stock and this
// panel all move together.
export default function JobMaterialsPanel({ job }) {
  const { user } = useAuth();
  // Printing may return excess material against its own job (same validated posting as Materials ever
  // did), but never issue new material to itself — that stays Stores' own call.
  const isPrinting = user?.role === "printing_operator";
  const canReturn = hasRole(user, "production_manager") || isPrinting;
  const canIssue = hasRole(user, "production_manager") && !isPrinting;
  const bpr = job.mpwo_number;
  const [ctx, setCtx] = useState(null);
  const [error, setError] = useState(null);
  const [legacy, setLegacy] = useState([]);
  const [dialog, setDialog] = useState(null); // "issue" | "return"

  const load = () => Promise.all([
    bpr
      ? api.get(`/stores/bpr/${encodeURIComponent(bpr)}`)
          .then(({ data }) => { setCtx(data); setError(null); })
          .catch((e) => setError(formatApiError(e)))
      : Promise.resolve(),
    // Entries from the earlier Stores I/O register, if this job has any.
    api.get("/inventory/stores-io", { params: { job_id: job.id, limit: 500 } })
      .then(({ data }) => setLegacy(data))
      .catch(() => setLegacy([])),
  ]);
  useEffect(() => { load(); }, [job.id, bpr]); // eslint-disable-line react-hooks/exhaustive-deps
  const reload = useLiveRefresh("stores", load);
  const done = () => { setDialog(null); reload(); };

  const remaining = ctx?.required ? Math.max(ctx.required - ctx.net_issued, 0) : null;
  const lines = ctx?.lines || [];

  return (
    <div className="space-y-4">
      <section className="border border-neutral-200 bg-white" data-testid="job-materials-panel">
        <div className="px-5 py-4 border-b border-neutral-200 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Package2 size={16} className="text-[#FF5A00]" />
            <div className="label-caps">Material issued against this job</div>
            {bpr && <span className="text-[11px] text-neutral-500 font-mono-num ml-1">BPR {bpr}</span>}
          </div>
          <div className="flex items-center gap-2">
            {bpr && canAccess(user, "/inventory") && (
              <Link to={bprMovementsPath(bpr)} className="text-xs text-[#FF5A00] hover:underline mr-1">All stores movements →</Link>
            )}
            {canReturn && bpr && (
              <Button size="sm" variant="outline" className="rounded-sm gap-1.5" onClick={() => setDialog("return")} data-testid="job-return-material-btn">
                <Undo2 size={13} /> Return
              </Button>
            )}
            {canIssue && bpr && (
              <Button size="sm" className={ORANGE_BTN} onClick={() => setDialog("issue")} data-testid="job-issue-material-btn">
                <PackageMinus size={13} /> Issue material
              </Button>
            )}
          </div>
        </div>

        {!bpr && (
          <div className="px-5 py-10 text-center text-sm text-neutral-400">
            This job has no BPR (MPWO) number yet, so stores can't issue against it.
          </div>
        )}
        {bpr && error && <div className="px-5 py-10 text-center text-sm text-neutral-500">{error}</div>}

        {ctx && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 border-b border-neutral-200 bg-neutral-50" data-testid="job-materials-summary">
              <Kpi label="Planned material" value={ctx.planned_item || ctx.job.substrate_code || "—"} sub={ctx.planned_item ? null : "no width on job card"} />
              <Kpi label="Required" value={ctx.required ? `${qty(ctx.required, 0)} m` : "—"} />
              <Kpi label="Issued" value={`${qty(ctx.issued, 0)} m`} tone="warn" />
              <Kpi label="Returned" value={`${qty(ctx.returned, 0)} m`} tone="ok" />
              <Kpi label="Net issued" value={`${qty(ctx.net_issued, 0)} m`}
                sub={ctx.net_issued < 0 ? "issued before the stores opening" : null} />
              <Kpi label={remaining ? "Still to issue" : "Printing consumed"}
                value={remaining ? `${qty(remaining, 0)} m` : ctx.consumed != null ? `${qty(ctx.consumed, 0)} m` : "—"}
                tone={remaining ? "danger" : undefined} />
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm [&_td]:whitespace-nowrap [&_th]:whitespace-nowrap" data-testid="job-materials-table">
                <thead className="bg-neutral-50 label-caps text-[10px]">
                  <tr>
                    <th className="text-left px-3 py-2.5">Date</th>
                    <th className="text-left px-3 py-2.5">Entry</th>
                    <th className="text-left px-3 py-2.5">Doc no.</th>
                    <th className="text-left px-3 py-2.5">Roll</th>
                    <th className="text-left px-3 py-2.5">Item</th>
                    <th className="text-left px-3 py-2.5">Dept</th>
                    <th className="text-right px-3 py-2.5">Qty</th>
                    <th className="text-left px-3 py-2.5">Remarks</th>
                  </tr>
                </thead>
                <tbody>
                  {lines.length === 0 && (
                    <tr><td colSpan={8} className="px-4 py-10 text-center text-neutral-400" data-testid="job-materials-empty">
                      Nothing issued to this BPR yet.
                    </td></tr>
                  )}
                  {[...lines].reverse().map((e) => (
                    <tr key={e.id} className={`border-t border-neutral-100 data-row ${e.reverses || e.reversed_by ? "text-neutral-400" : ""}`}
                      data-testid={`job-material-row-${e.id}`}>
                      <td className="px-3 py-2 font-mono-num text-xs">{e.date}</td>
                      <td className="px-3 py-2">
                        <span className={`px-1.5 py-0.5 rounded-sm text-[10px] uppercase tracking-widest border ${
                          e.txn_type === "issue" ? "bg-amber-50 text-amber-800 border-amber-200" : "bg-emerald-50 text-emerald-700 border-emerald-200"}`}>
                          {e.txn_type === "issue" ? "Issue" : "Return"}
                        </span>
                        {(e.reverses || e.reversed_by) && <span className="ml-1.5 text-[10px] uppercase tracking-widest">{e.reverses ? "reversal" : "reversed"}</span>}
                      </td>
                      <td className="px-3 py-2 font-mono-num text-xs">{e.doc_no && e.doc_no !== bpr ? e.doc_no : "—"}</td>
                      <td className="px-3 py-2"><RollsLink q={e.roll_no} className="font-mono-num text-xs" /></td>
                      <td className="px-3 py-2">
                        <RollsLink q={e.item_code} className="font-mono-num text-xs" />
                        <div className="text-[11px] text-neutral-500 truncate max-w-[14rem]">{e.item_name}</div>
                      </td>
                      <td className="px-3 py-2 text-xs">{e.department || "—"}</td>
                      <td className="px-3 py-2 text-right font-mono-num font-semibold">
                        {qty(e.qty)} <span className="text-[10px] text-neutral-500 font-normal">{unit(e.uom)}</span>
                      </td>
                      <td className="px-3 py-2 text-xs text-neutral-600 max-w-[16rem] truncate" title={e.remarks || e.reason || ""}>{e.remarks || e.reason || ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </section>

      {legacy.length > 0 && <LegacyStoresIO rows={legacy} />}

      {dialog === "issue" && <IssueDialog initialBpr={bpr} onClose={() => setDialog(null)} onDone={done} />}
      {dialog === "return" && <ReturnDialog initialBpr={bpr} onClose={() => setDialog(null)} onDone={done} />}
    </div>
  );
}

function Kpi({ label, value, sub, tone }) {
  const cls = tone === "danger" ? "text-rose-700" : tone === "warn" ? "text-amber-700" : tone === "ok" ? "text-emerald-700" : "text-neutral-900";
  return (
    <div className="border-r border-b md:border-b-0 border-neutral-200 last:border-r-0 px-5 py-3 min-w-0">
      <div className="label-caps mb-1 text-[10px]">{label}</div>
      <div className={`font-mono-num text-lg font-bold truncate ${cls}`} title={String(value)}>{value}</div>
      {sub && <div className="text-[11px] text-neutral-500">{sub}</div>}
    </div>
  );
}

/** Read-only: rows recorded through the earlier Stores I/O register for this job. */
function LegacyStoresIO({ rows }) {
  return (
    <section className="border border-neutral-200 bg-white" data-testid="job-materials-legacy">
      <div className="px-5 py-3 border-b border-neutral-200 label-caps">Earlier entries · Stores I/O register</div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm [&_td]:whitespace-nowrap [&_th]:whitespace-nowrap">
          <thead className="bg-neutral-50 label-caps text-[10px]">
            <tr>
              <th className="text-left px-3 py-2.5">Date</th>
              <th className="text-left px-3 py-2.5">Material</th>
              <th className="text-left px-3 py-2.5">Code</th>
              <th className="text-right px-3 py-2.5">Qty</th>
              <th className="text-right px-3 py-2.5">Return</th>
              <th className="text-left px-3 py-2.5">Direction</th>
              <th className="text-left px-3 py-2.5">Dept</th>
              <th className="text-left px-3 py-2.5">Entered by</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-neutral-100 data-row">
                <td className="px-3 py-2 font-mono-num text-xs text-neutral-600">{(r.date || "").slice(0, 10)}</td>
                <td className="px-3 py-2">{r.material_name}</td>
                <td className="px-3 py-2 font-mono-num text-xs">{r.material_code || "—"}</td>
                <td className="px-3 py-2 text-right font-mono-num">{inr(r.qty)} <span className="text-[10px] text-neutral-500">{r.uom}</span></td>
                <td className="px-3 py-2 text-right font-mono-num">{r.return_qty != null ? inr(r.return_qty) : "—"}</td>
                <td className="px-3 py-2 text-xs">{r.direction || "outward"}</td>
                <td className="px-3 py-2 text-xs">{r.dept_to || "—"}</td>
                <td className="px-3 py-2 text-xs text-neutral-600">{r.user_name || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
